import { action, computed, observable, runInAction } from 'mobx';
import { API, FetchStatus } from '../util/ApiUtil';
import ErrorUtil from '../util/ErrorUtil';
import { ConfirmationType } from '../account/RemovalConfirmationModal';
import { waitForModalAnimation } from '../util/AnimationUtil';

export interface EmailAccount {
  address: string;
}

export interface InfoRemovalData {
  type: ConfirmationType | null;
  data: string | null;
}

export default class AccountInfoStore {
  @observable accessor firstName: string = '';
  @observable accessor lastName: string = '';
  @observable accessor personalCode: string = '';
  @observable accessor showAddPersonalCodeButton: boolean = false;
  @observable accessor isSessionValid: boolean = false;
  @observable accessor emails: EmailAccount[] = [];
  @observable accessor fetchStatus: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor removalModalOpen: boolean = false;
  @observable accessor removalModalData: InfoRemovalData = { type: null, data: null };

  @computed
  public get isLoading() {
    return [FetchStatus.FETCHING, FetchStatus.UNINITIALIZED].includes(this.fetchStatus);
  }

  @action.bound
  public removeEmailFromList(email: string) {
    this.emails = this.emails.filter((e) => e.address !== email);
  }

  public setRemovalModalState = (isOpen: boolean) => {
    if (!isOpen) {
      runInAction(() => {
        this.removalModalOpen = false;
      });
      waitForModalAnimation(() => (this.removalModalData = { type: null, data: null }));
      return;
    }
    runInAction(() => {
      this.removalModalOpen = true;
    });
  };

  public startAccountInfoRemoval = async (type: ConfirmationType, data: string) => {
    runInAction(() => {
      this.removalModalOpen = true;
      this.removalModalData = { type, data };
    });
  };

  public async loadInitialInfo() {
    try {
      runInAction(() => {
        this.fetchStatus = FetchStatus.FETCHING;
      });
      const response = await API.get('/account/info');
      runInAction(() => {
        this.firstName = response?.data?.firstName;
        this.lastName = response?.data?.lastName;
        this.personalCode = response?.data?.personalCode;
        this.showAddPersonalCodeButton = response?.data?.showAddPersonalCodeButton;
        this.isSessionValid = response?.data?.isSessionValid;
        this.emails = response?.data?.emails;
        this.fetchStatus = FetchStatus.FETCHED;
      });
    } catch (e: Error | string | any) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      runInAction(() => {
        this.fetchStatus = FetchStatus.ERROR;
      });
    }
  }

  public async loadInitialEmailAccounts() {
    try {
      const response = await API.get('/account/info');
      runInAction(() => {
        this.emails = response?.data?.emails;
      });
    } catch (e: Error | string | any) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
    }
  }
}
